/** Tutorial staged pipeline — step execution and resume helpers. */

import {
  LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
  LEARNING_TUTORIAL_SYNC_TIMEOUT_MS,
  LEARNING_TUTORIAL_TIMELINE_TIMEOUT_MS,
} from '@/lib/learningTutorial/apiTimeouts';
import {
  mapBackendPipelineStep,
  tutorialPipelineStepFromProject,
} from '@/lib/learningTutorial/tutorialHistory';
import { isStepComplete, TUTORIAL_PIPELINE_STEPS } from '@/lib/learningTutorial/pipelineSteps';

export const TUTORIAL_RUN_STEPS = [
  'pass1',
  'pass2',
  'pass2_optimize',
  'script',
  'narration_audio',
  'word_align',
  'timeline',
  'artifact_embed',
];

export { mapBackendPipelineStep };

/**
 * Determine which step should be retried.
 * @param {{ project?: object | null, currentStepId?: string | null, pipelineStatus?: string }} ctx
 */
/** True when TTS ran but per-clip assets were not stored (WhisperX cannot proceed). */
export function needsNarrationClipRerun(project) {
  if (!project?.script) return false;
  const clips = project?.narration_clip_assets || [];
  return clips.length === 0;
}

/** True when script exists but audio pipeline has not finished (TTS → WhisperX → embed). */
export function needsAudioPipelineRun(project) {
  if (!project?.script) return false;
  const clips = project?.narration_clip_assets || [];
  const phase = project?.narration_sync_phase;
  if (clips.length === 0) return true;
  if (phase === 'complete' || project?.rendered_timeline?.trainer_artifacts_embedded) {
    return false;
  }
  return true;
}

export function resolveResumeStep({ project, currentStepId, pipelineStatus }) {
  if (needsNarrationClipRerun(project)) {
    return 'narration_audio';
  }
  if (pipelineStatus === 'error' && currentStepId && TUTORIAL_RUN_STEPS.includes(currentStepId)) {
    return currentStepId;
  }
  if (project?.last_pipeline_status === 'failed') {
    const fromBackend = mapBackendPipelineStep(project.last_pipeline_step);
    if (fromBackend) return fromBackend;
  }
  const fromProject = tutorialPipelineStepFromProject(project);
  if (fromProject && fromProject !== 'done' && TUTORIAL_RUN_STEPS.includes(fromProject)) {
    return fromProject;
  }
  if (currentStepId && TUTORIAL_RUN_STEPS.includes(currentStepId) && project) {
    if (!isStepComplete(currentStepId, project, orchestrationFromProject(project))) {
      return currentStepId;
    }
  }
  return null;
}

function orchestrationFromProject(project) {
  return project?.orchestration || null;
}

/** Steps to run after (and including) startStep when continuing. */
export function stepsFrom(startStepId) {
  const idx = TUTORIAL_RUN_STEPS.indexOf(startStepId);
  if (idx < 0) return [];
  return TUTORIAL_RUN_STEPS.slice(idx);
}

/** Steps strictly after startStep (for continue-after-retry). */
export function stepsAfter(startStepId) {
  const idx = TUTORIAL_RUN_STEPS.indexOf(startStepId);
  if (idx < 0) return [];
  return TUTORIAL_RUN_STEPS.slice(idx + 1);
}

export function canResumePipeline({ projectId, project, pipelineStatus, currentStepId, orchestration }) {
  if (!projectId || !project) return false;
  if (pipelineStatus === 'working') return false;
  if (needsNarrationClipRerun(project)) return true;
  if (pipelineStatus === 'ready') return false;
  const resumeStep = resolveResumeStep({ project, currentStepId, pipelineStatus });
  if (!resumeStep) return false;
  if (isStepComplete(resumeStep, project, orchestration) && pipelineStatus !== 'error') {
    const next = stepsAfter(resumeStep)[0];
    return Boolean(next && !isStepComplete(next, project, orchestration));
  }
  return !isStepComplete(resumeStep, project, orchestration) || pipelineStatus === 'error';
}

export function resumeStepMeta(stepId) {
  const step = TUTORIAL_PIPELINE_STEPS.find((s) => s.id === stepId);
  return step || { id: stepId, shortLabel: stepId, label: stepId };
}

function isRequestTimeout(err) {
  return err?.code === 'ECONNABORTED' || /timeout/i.test(String(err?.message || ''));
}

/** True when the server already persisted this orchestration step. */
export function orchestrationStepCompleteOnProject(project, stepId) {
  const orch = project?.orchestration;
  if (!orch) return false;
  const pass = Number(orch.pipeline_pass || 0);
  const passStatus = orch.pass_status || {};
  if (stepId === 'pass1') {
    return pass >= 1 || Boolean(orch.concept_tree?.length);
  }
  if (stepId === 'pass2') {
    return pass >= 2
      || passStatus.pass2 === 'complete'
      || passStatus.pass2 === 'partial'
      || passStatus.pass2 === 'fallback'
      || Boolean(orch.derived?.topic_tree?.length);
  }
  if (stepId === 'pass2_optimize') {
    return passStatus.pass2_optimize === 'complete'
      || passStatus.pass2_optimize === 'fallback'
      || Boolean(orch.pass_history?.pass2_optimize);
  }
  if (stepId === 'script') {
    return pass >= 3
      || passStatus.pass3 === 'complete'
      || passStatus.pass3 === 'fallback'
      || passStatus.build_script === 'complete'
      || passStatus.build_script === 'fallback'
      || Boolean(project?.script || orch.script);
  }
  return false;
}

async function postOrchestrationStep(apiClient, projectId, path, stepId) {
  try {
    const { data } = await apiClient.post(path, null, {
      timeout: LEARNING_TUTORIAL_ORCHESTRATION_TIMEOUT_MS,
    });
    return data;
  } catch (err) {
    if (!isRequestTimeout(err)) throw err;
    const { data } = await apiClient.get(`/learning-tutorials/projects/${projectId}`);
    const full = data.project;
    if (orchestrationStepCompleteOnProject(full, stepId)) {
      return { orchestration: full.orchestration, project: full, recovered: true };
    }
    throw err;
  }
}

async function postNarrationStep(apiClient, projectId, path, stepId) {
  try {
    const { data } = await apiClient.post(path, null, {
      timeout: LEARNING_TUTORIAL_SYNC_TIMEOUT_MS,
    });
    return data;
  } catch (err) {
    if (!isRequestTimeout(err)) throw err;
    const { data } = await apiClient.get(`/learning-tutorials/projects/${projectId}`);
    const full = data.project;
    if (isStepComplete(stepId, full, full?.orchestration)) {
      return { project: full, recovered: true };
    }
    throw err;
  }
}

/**
 * Execute a single pipeline step.
 * @returns {Promise<{ orchestration?: object, project?: object, toastMessage?: string }>}
 */
export async function runTutorialStep(apiClient, projectId, stepId) {
  switch (stepId) {
    case 'pass1': {
      const data = await postOrchestrationStep(
        apiClient,
        projectId,
        `/learning-tutorials/projects/${projectId}/orchestration/pass1`,
        'pass1',
      );
      return {
        orchestration: data.orchestration,
        project: data.project,
        toastMessage: data.recovered
          ? 'Pass 1 — concept tree ready (finished on server after browser timeout)'
          : 'Pass 1 — concept tree ready',
      };
    }
    case 'pass2': {
      const data = await postOrchestrationStep(
        apiClient,
        projectId,
        `/learning-tutorials/projects/${projectId}/orchestration/pass2`,
        'pass2',
      );
      return {
        orchestration: data.orchestration,
        project: data.project,
        toastMessage: data.recovered
          ? 'Pass 2 — artifacts expanded (finished on server after browser timeout)'
          : 'Pass 2 — artifacts expanded',
      };
    }
    case 'pass2_optimize': {
      const data = await postOrchestrationStep(
        apiClient,
        projectId,
        `/learning-tutorials/projects/${projectId}/orchestration/pass2-optimize`,
        'pass2_optimize',
      );
      return {
        orchestration: data.orchestration,
        project: data.project,
        toastMessage: data.recovered
          ? 'Pass 2 optimize — topics deduplicated (finished on server after browser timeout)'
          : 'Pass 2 optimize — topics deduplicated',
      };
    }
    case 'script': {
      const data = await postOrchestrationStep(
        apiClient,
        projectId,
        `/learning-tutorials/projects/${projectId}/orchestration/build-script`,
        'script',
      );
      return {
        orchestration: data.orchestration,
        project: data.project,
        toastMessage: data.recovered
          ? 'Pass 3 — trainer script ready (finished on server after browser timeout)'
          : 'Pass 3 — trainer script ready',
      };
    }
    case 'timeline': {
      const { data: pre } = await apiClient.get(`/learning-tutorials/projects/${projectId}`);
      const preProject = pre.project;
      if (!isStepComplete('word_align', preProject, preProject?.orchestration)) {
        if (!isStepComplete('narration_audio', preProject, preProject?.orchestration)) {
          await runTutorialStep(apiClient, projectId, 'narration_audio');
        }
        await runTutorialStep(apiClient, projectId, 'word_align');
      }
      await apiClient.post(`/learning-tutorials/projects/${projectId}/generate-timeline`, null, {
        timeout: LEARNING_TUTORIAL_TIMELINE_TIMEOUT_MS,
      });
      const { data } = await apiClient.get(`/learning-tutorials/projects/${projectId}`);
      const full = data.project;
      const blockCount = (full?.timeline?.blocks || []).length;
      return {
        project: full,
        toastMessage: `Timeline — ${blockCount} block(s)`,
      };
    }
    case 'narration_audio': {
      const data = await postNarrationStep(
        apiClient,
        projectId,
        `/learning-tutorials/projects/${projectId}/narration-audio`,
        'narration_audio',
      );
      return {
        project: data.project,
        toastMessage: data.recovered
          ? 'Narration audio ready (finished on server after browser timeout)'
          : 'Narration audio generated from trainer script',
      };
    }
    case 'word_align': {
      const data = await postNarrationStep(
        apiClient,
        projectId,
        `/learning-tutorials/projects/${projectId}/narration-align`,
        'word_align',
      );
      return {
        project: data.project,
        toastMessage: data.recovered
          ? 'WhisperX alignment ready (finished on server after browser timeout)'
          : 'WhisperX word alignment complete',
      };
    }
    case 'artifact_embed': {
      const data = await postNarrationStep(
        apiClient,
        projectId,
        `/learning-tutorials/projects/${projectId}/narration-embed-artifacts`,
        'artifact_embed',
      );
      return {
        project: data.project,
        toastMessage: data.recovered
          ? 'Artifacts embedded (finished on server after browser timeout)'
          : 'Trainer artifacts embedded on timeline',
      };
    }
    case 'narration': {
      const data = await postNarrationStep(
        apiClient,
        projectId,
        `/learning-tutorials/projects/${projectId}/preview-narration`,
        'artifact_embed',
      );
      return {
        project: data.project,
        toastMessage: data.recovered
          ? 'Narration aligned (finished on server after browser timeout)'
          : 'Narration aligned',
      };
    }
    default:
      throw new Error(`Unknown pipeline step: ${stepId}`);
  }
}

export function pipelineStatusAfterStep(stepId, project, orchestration) {
  if (stepId === 'artifact_embed' && isStepComplete('artifact_embed', project, orchestration)) {
    return { status: 'ready', currentStep: 'done' };
  }
  if (isStepComplete(stepId, project, orchestration)) {
    const remaining = stepsAfter(stepId);
    if (remaining.length === 0) {
      return { status: 'ready', currentStep: 'done' };
    }
    return { status: 'idle', currentStep: remaining[0] };
  }
  return { status: 'idle', currentStep: stepId };
}
